/**
 * Aseos API module.
 *
 * Covers the full cleaning lifecycle: fetch, create, update, delete,
 * bulk-pay, and the aseadora take/reject actions (fire-and-forget).
 *
 * All functions delegate to `apiFetch`, which handles auth headers,
 * JSON parsing and Ant Design error/success toasts automatically.
 */

import { apiFetch } from "./apiClient";
import { AseoResponse, ApiResponse } from "../interfaces/api.interfaces";

// ─── Types ────────────────────────────────────────────────────────────────────

/** Payload accepted by POST /aseos and PUT /aseos/:id */
export interface AseoPayload {
  idPropiedad?: number;
  idAseador?: number | null;
  fechaInicio?: string | null;
  fechaFin?: string | null;
  horaInicio?: string | null;
  estado?: string;
  urgencia?: string;
  observaciones?: string;
  costoLimpieza?: number;
  sueldoAseador?: number;
  modoAsignacion?: "designado" | "abierto";
  asignacionActiva?: boolean;
  /**
   * Round 2026-08-25 — reparto del sueldo entre 1..N aseadoras. Responsable
   * primero; sin montos = el backend reparte en partes iguales; con montos,
   * la suma debe calzar EXACTA con sueldoAseador (400 si no).
   */
  asignaciones?: Array<{ usuario_id: number; monto?: number | null }>;
  [key: string]: any;
}

// ─── Notas del aseo (3 notas con adjuntos) — round 2026-07-05 ──────────────────

export type AseoNoteTipo = "privada" | "para_aseador" | "aseador";

/** Entrada de adjunto (foto/video). URLs firmadas por el backend al leer. */
export interface AseoFileEntry {
  key: string;
  provider?: string;
  thumbKey?: string | null;
  originalName?: string;
  mimeType?: string;
  size?: number;
  kind?: "image" | "video" | "doc" | string;
  url?: string;
  thumbUrl?: string | null;
  /** URL firmada de la versión liviana del video (480p); `null` si no existe en disco. */
  previewUrl?: string | null;
  /** Peso (bytes) de la versión liviana, si existe. */
  previewSize?: number | null;
}

export interface AseoNote {
  texto: string;
  archivos: AseoFileEntry[];
}

export interface AseoNotesData {
  notas: Partial<Record<AseoNoteTipo, AseoNote>>;
  permisos: Record<AseoNoteTipo, boolean>;
}

/**
 * Ruta (relativa a la API) del POST que agrega un adjunto a una nota. La usa
 * la cola de subida (`utils/subidaReanudable.ts`): el mismo endpoint recibe el
 * archivo por multipart o, para archivos grandes, con `?uploadId=`.
 */
export const rutaArchivosNota = (aseoId: number, tipo: AseoNoteTipo): string =>
  `/aseos/${aseoId}/notas/${tipo}/archivos`;

export const aseoNotesApi = {
  /**
   * GET /aseos/:id/notas — notas visibles para el caller (URLs firmadas).
   * `silent`: el componente muestra su propio estado de error con botón
   * Reintentar (un toast con "Failed to fetch" no le sirve a nadie en terreno).
   */
  get: (aseoId: number): Promise<ApiResponse<AseoNotesData> | null> =>
    apiFetch<ApiResponse<AseoNotesData>>(`/aseos/${aseoId}/notas`, {
      silent: true,
    }),

  /**
   * PUT /aseos/:id/notas/:tipo — edita el texto de una nota.
   * `opts.silent` omite el toast de éxito — se usa al "flushear" las notas
   * en borrador tras crear un aseo (evita 3 toasts "Nota guardada" seguidos;
   * el toast "Aseo creado" ya cubre el feedback).
   * `opts.keepalive`: deja que la petición termine aunque la página se cierre u
   * oculte (autoguardado al bloquear el celular). El navegador limita el cuerpo
   * a 64 KB; el texto de una nota cabe de sobra.
   * `opts.sinToastError`: tampoco muestra el toast de error (que en una red caída
   * trae el "Failed to fetch" crudo del navegador, en inglés). Lo usa el
   * autoguardado, que ya avisa con su propio texto en español y reintenta solo.
   */
  updateTexto: (
    aseoId: number,
    tipo: AseoNoteTipo,
    texto: string,
    opts?: { silent?: boolean; keepalive?: boolean; sinToastError?: boolean },
  ): Promise<ApiResponse<AseoNote> | null> =>
    apiFetch<ApiResponse<AseoNote>>(`/aseos/${aseoId}/notas/${tipo}`, {
      method: "PUT",
      body: JSON.stringify({ texto }),
      ...(opts?.keepalive ? { keepalive: true } : {}),
      ...(opts?.silent ? {} : { successMsg: "Nota guardada" }),
      ...(opts?.sinToastError ? { silent: true } : {}),
      errorMsg: "Error al guardar la nota",
    }),

  /** POST /aseos/:id/notas/:tipo/archivos — sube una foto/video (FormData). */
  uploadArchivo: (
    aseoId: number,
    tipo: AseoNoteTipo,
    file: File,
  ): Promise<ApiResponse<AseoFileEntry> | null> => {
    const fd = new FormData();
    fd.append("file", file);
    return apiFetch<ApiResponse<AseoFileEntry>>(
      `/aseos/${aseoId}/notas/${tipo}/archivos`,
      {
        method: "POST",
        body: fd,
        errorMsg: "Error al subir el archivo",
      },
    );
  },

  /**
   * DELETE /aseos/:id/notas/:tipo/archivos?key= — borra un adjunto.
   * `silent`: el componente explica el fallo en español (sin el "Failed to
   * fetch" crudo del navegador); el éxito sí muestra su toast.
   */
  deleteArchivo: (
    aseoId: number,
    tipo: AseoNoteTipo,
    key: string,
  ): Promise<ApiResponse<AseoFileEntry[]> | null> =>
    apiFetch<ApiResponse<AseoFileEntry[]>>(
      `/aseos/${aseoId}/notas/${tipo}/archivos?key=${encodeURIComponent(key)}`,
      {
        method: "DELETE",
        successMsg: "Archivo eliminado",
        silent: true,
      },
    ),
};

// ─── Aseos el mismo día (advertencia al asignar) — round 2026-09-11 ───────────

/** Un aseo que la aseadora ya tiene en los días consultados. */
export interface AseoMismoDia {
  id: number;
  propiedad: string | null;
  fechaInicio: string | null;
  fechaTermino: string | null;
  horaAseo: string | null;
  estado: string | null;
}

/** Los aseos de UNA aseadora consultada en esos días (solo vienen las que tienen alguno). */
export interface CruceMismoDia {
  usuarioId: number;
  nombre: string;
  aseos: AseoMismoDia[];
}

// ─── aseosApi ─────────────────────────────────────────────────────────────────

export const aseosApi = {
  /** GET /aseos — returns all cleanings. */
  getAll: (): Promise<AseoResponse | null> =>
    apiFetch<AseoResponse>("/aseos", {
      errorMsg: "Error al obtener los aseos",
    }),

  /** GET /aseos/calendario */
  getCalendario: (): Promise<AseoResponse | null> =>
    apiFetch<AseoResponse>("/aseos/calendario", {
      errorMsg: "Error al obtener el calendario de aseos",
    }),

  /** POST /aseos */
  create: (payload: AseoPayload): Promise<ApiResponse<any> | null> =>
    apiFetch<ApiResponse<any>>("/aseos", {
      method: "POST",
      body: JSON.stringify(payload),
      successMsg: "Aseo creado correctamente",
      errorMsg: "Error al crear el aseo",
    }),

  /** PUT /aseos/:id */
  update: (
    id: number,
    payload: AseoPayload,
  ): Promise<ApiResponse<any> | null> =>
    apiFetch<ApiResponse<any>>(`/aseos/${id}`, {
      method: "PUT",
      body: JSON.stringify(payload),
      successMsg: "Aseo actualizado correctamente",
      errorMsg: "Error al actualizar el aseo",
    }),

  /** DELETE /aseos/:id */
  delete: (id: number): Promise<any | null> =>
    apiFetch(`/aseos/${id}`, {
      method: "DELETE",
      successMsg: "Aseo eliminado correctamente",
      errorMsg: "Error al eliminar el aseo",
    }),

  /** PATCH /aseos/pagar-todo — marks all cleanings as paid. */
  pagarTodo: (): Promise<any | null> =>
    apiFetch("/aseos/pagar-todo", {
      method: "PATCH",
      successMsg: "Todos los aseos marcados como pagados",
      errorMsg: "Error al marcar todos como pagados",
    }),

  /**
   * PUT /aseos/tomar/:id — aseadora claims an open cleaning.
   * Devuelve la respuesta, o `null` si el servidor lo negó (409: ya tiene dueña, o es un abierto
   * fuera de la ventana de 14 días con el aviso de cuándo se habilita). `apiFetch` ya mostró el
   * mensaje del servidor; el que llama NO debe dar el aseo por tomado si recibe `null`.
   */
  tomar: (aseoId: number, aseadoraId: number): Promise<any | null> =>
    apiFetch(`/aseos/tomar/${aseoId}`, {
      method: "PUT",
      body: JSON.stringify({ aseadoraId }),
    }),

  /**
   * Round 2026-05-12 iter 39 — bulk actions.
   * Cada endpoint envuelve en transacción y emite UN solo broadcast WS al
   * final. Validan ids no vacíos y el valor (estado/urgencia) contra la
   * lista de valores aceptados por el modelo.
   */

  /** PATCH /aseos/bulk-estado */
  bulkEstado: (
    ids: number[],
    estado: "pendiente" | "en_proceso" | "completado" | "pagado",
  ): Promise<{ success: boolean; updated: number } | null> =>
    apiFetch(`/aseos/bulk-estado`, {
      method: "PATCH",
      body: JSON.stringify({ ids, estado }),
      successMsg: `Estado actualizado en ${ids.length} aseos`,
      errorMsg: "Error al actualizar el estado en bulk",
    }),

  /** PATCH /aseos/bulk-urgencia */
  bulkUrgencia: (
    ids: number[],
    urgencia: "am" | "pm" | "critico 11:00" | "11:00",
  ): Promise<{ success: boolean; updated: number } | null> =>
    apiFetch(`/aseos/bulk-urgencia`, {
      method: "PATCH",
      body: JSON.stringify({ ids, urgencia }),
      successMsg: `Urgencia actualizada en ${ids.length} aseos`,
      errorMsg: "Error al actualizar la urgencia en bulk",
    }),

  /** POST /aseos/bulk-delete */
  bulkDelete: (
    ids: number[],
  ): Promise<{ success: boolean; deleted: number } | null> =>
    apiFetch(`/aseos/bulk-delete`, {
      method: "POST",
      body: JSON.stringify({ ids }),
      successMsg: `Eliminados ${ids.length} aseos`,
      errorMsg: "Error al eliminar en bulk",
    }),

  /**
   * GET /aseos/mismo-dia — ¿estas aseadoras ya tienen OTRO aseo esos días?
   * Alimenta la advertencia de los modales que asignan (round 2026-09-11).
   * `silent`: es una ayuda; si falla no molesta con toasts ni frena el guardado.
   */
  mismoDia: (consulta: {
    usuarioIds: number[];
    desde: string;
    hasta: string;
    excluirAseoId?: number | null;
  }): Promise<ApiResponse<CruceMismoDia[]> | null> => {
    const qs = new URLSearchParams({
      usuarios: consulta.usuarioIds.join(","),
      desde: consulta.desde,
      hasta: consulta.hasta,
    });
    if (consulta.excluirAseoId != null) {
      qs.set("excluir", String(consulta.excluirAseoId));
    }
    return apiFetch<ApiResponse<CruceMismoDia[]>>(`/aseos/mismo-dia?${qs.toString()}`, {
      silent: true,
    });
  },
};
